import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spin, Empty, message, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { AlipayIcon, WechatIcon } from '../components/PaymentIcons';

const GOLD         = '#B8892A';
const BD           = '#E8E0D0';
const BG           = '#FAFAF8';
const MUTED        = '#6B5E4E';
const MUTED_LIGHT  = '#A8998A';
const GREEN        = '#2A6645';
const RED          = '#9B2335';
const ORANGE       = '#C96B10';

const QUICK_AMOUNTS = [500, 1000, 3000, 5000, 10000, 20000, 50000, 0];

const PAY_METHODS = [
  { key: 'wechat',        icon: <WechatIcon size={24} />,            labelKey: 'deposit.wechat' },
  { key: 'alipay',        icon: <AlipayIcon size={24} />,            labelKey: 'deposit.alipay' },
  { key: 'bank_transfer', icon: <span style={{ fontSize: 24 }}>🏦</span>, labelKey: 'deposit.bankTransfer' },
];

// 与后端 deposit_ledger.type ENUM 完全对齐
const LEDGER_TYPE_MAP = {
  deposit         : { labelKey: 'deposit.recharge',         color: GREEN,       bg: 'rgba(42,102,69,0.1)' },
  withdrawal      : { labelKey: 'deposit.refund',         color: '#8A6010',   bg: 'rgba(184,134,11,0.1)' },
  freeze          : { labelKey: 'deposit.frozen',   color: '#8A6010',   bg: 'rgba(184,134,11,0.1)' },
  unfreeze        : { labelKey: 'deposit.available',   color: '#1A4F8C',   bg: 'rgba(26,79,140,0.1)' },
  deduct_win      : { labelKey: 'mandates.statusWon',     color: RED,         bg: 'rgba(155,35,53,0.1)' },
  refund          : { labelKey: 'deposit.refund',         color: GREEN,       bg: 'rgba(42,102,69,0.1)' },
  forfeit_deposit : { labelKey: 'deposit.title',   color: RED,         bg: 'rgba(155,35,53,0.1)' },
};

// Status display config for deposit requests
const REQUEST_STATUS_MAP = {
  unpaid   : { labelKey: 'deposit.statusPending',       color: RED,    bg: 'rgba(155,35,53,0.08)' },
  paid     : { labelKey: 'deposit.statusPending', color: ORANGE, bg: 'rgba(201,107,16,0.08)' },
  confirmed: { labelKey: 'deposit.statusCompleted',       color: GREEN,  bg: 'rgba(42,102,69,0.08)' },
  rejected : { labelKey: 'deposit.statusFailed',       color: MUTED_LIGHT, bg: 'rgba(168,153,138,0.08)' },
};

function formatCNY(val) {
  if (val == null) return '—';
  return '¥' + Number(val).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
function isIncome(type) {
  return ['deposit', 'unfreeze', 'refund'].includes(type);
}

const METHOD_LABEL = { wechat: '微信支付', alipay: '支付宝', bank_transfer: '银行转账' };

/* ── 倒计时 hook（返回剩余秒数，方便外部判断是否超时）── */
function useCountdown(active, seconds = 298) {
  const [secs, setSecs] = useState(seconds);
  const ref = useRef(null);
  useEffect(() => {
    if (!active) { setSecs(seconds); return; }
    setSecs(seconds);
    ref.current = setInterval(() => setSecs(s => {
      if (s <= 1) { clearInterval(ref.current); return 0; }
      return s - 1;
    }), 1000);
    return () => clearInterval(ref.current);
  }, [active, seconds]);
  return secs; // 返回剩余秒数，0 = 已超时
}

/* ── 支付画面（二维码 / 银行信息）── */
function PayingScreen({ request, payConfig, onSuccess, onBack }) {
  const { t } = useTranslation();
  const { id, amount_cny, payment_method, qr_code_url, wx_h5_url } = request;
  const isBank   = payment_method === 'bank_transfer';
  const isAlipay = payment_method === 'alipay';
  const isWechat = payment_method === 'wechat';
  const isQrPay  = isAlipay || isWechat;
  const remainSecs = useCountdown(isQrPay); // 剩余秒数，0=超时
  const isExpired = isQrPay && remainSecs === 0;
  const countdown = remainSecs === 0 ? '已过期'
    : String(Math.floor(remainSecs / 60)).padStart(2, '0') + ':' + String(remainSecs % 60).padStart(2, '0');
  const pollRef   = useRef(null);
  const [markingPaid, setMarkingPaid] = useState(false);

  // H5 支付(手机浏览器)：拿到 h5_url 立即跳转唤起微信 App。
  // 带 redirect_url 让付完自动跳回充值页(?checking=<id>)继续轮询查单。
  useEffect(() => {
    if (wx_h5_url) {
      const redirect = encodeURIComponent(window.location.origin + '/deposit?checking=' + id);
      window.location.href = wx_h5_url + (wx_h5_url.includes('?') ? '&' : '?') + 'redirect_url=' + redirect;
    }
  }, [wx_h5_url, id]);

  // Poll for payment status — 超时后自动停止轮询
  useEffect(() => {
    if (!isQrPay || isExpired) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/deposits/requests/${id}`);
        if (data.status === 'paid' || data.status === 'confirmed') {
          clearInterval(pollRef.current);
          onSuccess(data);
        }
      } catch {}
    }, 4000);
    return () => clearInterval(pollRef.current);
  // isExpired 变化时重新执行，超时后即停止
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isQrPay, isExpired, onSuccess]);

  const handleMarkPaid = async () => {
    setMarkingPaid(true);
    try {
      await api.post(`/deposits/requests/${id}/mark-paid`);
      onSuccess({ ...request, status: 'paid' });
    } catch (err) {
      message.error(err?.response?.data?.error || t('common.failed'));
    } finally {
      setMarkingPaid(false);
    }
  };

  return (
    <div style={{ maxWidth: 480, margin: '0 auto' }}>
      {/* 订单号 */}
      <div style={{
        background: '#fff', border: `1px solid ${BD}`, borderRadius: 6,
        padding: '16px 20px', marginBottom: 16,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.3em', color: MUTED_LIGHT, marginBottom: 4 }}>ORDER NO · 订单号</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 13, color: '#110E0A', fontWeight: 600, letterSpacing: '0.1em' }}>{id}</div>
        </div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 22, color: GOLD, fontWeight: 700 }}>
          ¥{Number(amount_cny).toLocaleString()}
        </div>
      </div>

      {/* 支付内容 */}
      <div style={{ background: '#fff', border: `1px solid ${BD}`, borderRadius: 6, overflow: 'hidden', marginBottom: 16 }}>
        {/* 头部 */}
        <div style={{
          padding: '14px 20px', borderBottom: `1px solid ${BD}`,
          background: 'linear-gradient(135deg,#faf8f3,#f3ede3)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isBank ? <span style={{ fontSize: 20 }}>🏦</span> : isWechat ? <WechatIcon size={20} /> : <AlipayIcon size={20} />}
            <span style={{ fontFamily: 'var(--serif)', fontSize: 15, fontWeight: 600, color: '#110E0A' }}>
              {isBank ? '银行转账' : isWechat ? '微信支付' : '支付宝'}
            </span>
          </div>
          {isQrPay && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'var(--mono)', fontSize: 10, color: isExpired ? RED : GREEN }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: isExpired ? RED : GREEN, display: 'inline-block' }} />
              {isExpired ? '二维码已过期' : `${countdown} 后过期`}
            </div>
          )}
        </div>

        <div style={{ padding: 24 }}>
          {isBank ? (
            /* 银行转账信息 */
            <>
              {[
                ['收款银行', payConfig?.bank_name || '—'],
                payConfig?.bank_branch ? ['支行', payConfig.bank_branch] : null,
                ['账户名',   payConfig?.bank_account_name || '—'],
                ['账号',     payConfig?.bank_account_no   || '—'],
                ['转账金额', `¥${Number(amount_cny).toLocaleString()}`],
              ].filter(Boolean).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid rgba(232,224,208,0.5)`, fontFamily: 'var(--mono)', fontSize: 13 }}>
                  <span style={{ color: MUTED_LIGHT }}>{k}</span>
                  <span style={{ color: k === '转账金额' ? GOLD : '#110E0A', fontWeight: k === '转账金额' ? 700 : 500 }}>{v}</span>
                </div>
              ))}
              <div style={{ marginTop: 16, padding: '14px 16px', background: 'rgba(155,35,53,0.05)', border: `1px solid rgba(155,35,53,0.2)`, borderRadius: 4 }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: RED, lineHeight: 1.8 }}>
                  ⚠ 请在汇款备注填写您的注册手机号，否则无法匹配到账
                </div>
              </div>
              <div style={{ marginTop: 12, fontFamily: 'var(--mono)', fontSize: 11, color: MUTED_LIGHT, lineHeight: 1.8 }}>
                到账时间：工作日 1-2 小时内（人工审核）
              </div>
              {/* 汇款完成按钮 */}
              <button
                onClick={handleMarkPaid}
                disabled={markingPaid}
                style={{
                  marginTop: 20, width: '100%', padding: '14px 0',
                  background: markingPaid ? '#ccc' : GOLD, border: 'none', borderRadius: 4,
                  color: '#fff', fontFamily: 'var(--serif)', fontSize: 15, fontWeight: 600,
                  letterSpacing: '0.15em', cursor: markingPaid ? 'not-allowed' : 'pointer',
                }}
              >
                {markingPaid ? '提交中…' : '✓ 汇款完成，等待审核'}
              </button>
            </>
          ) : (
            /* 扫码/H5支付（微信/支付宝） */
            <>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                {isWechat && !qr_code_url ? (
                  /* 微信H5支付：跳转前占位/回跳后等待支付结果 */
                  <div style={{ padding: '30px 20px' }}>
                    <WechatIcon size={64} />
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 14, color: '#110E0A', fontWeight: 600, marginTop: 16 }}>
                      {wx_h5_url ? '正在跳转微信支付…' : '等待微信支付结果'}
                    </div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: MUTED_LIGHT, marginTop: 8, lineHeight: 1.8 }}>
                      {wx_h5_url
                        ? <>即将唤起微信 App 完成支付<br />未跳转请检查是否安装微信</>
                        : <>如已在微信中完成支付，请稍候…<br />系统正在自动查询支付结果</>}
                    </div>
                    <Spin style={{ marginTop: 16 }} />
                  </div>
                ) : (
                  /* 支付宝二维码 */
                  <>
                    <div style={{
                      width: 200, height: 200, margin: '0 auto',
                      background: '#f8f5ef', border: `1px solid ${BD}`, borderRadius: 6,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                      position: 'relative',
                    }}>
                      {qr_code_url ? (
                        <>
                          <img src={qr_code_url} alt={isWechat ? '微信收款码' : '支付宝收款码'} style={{ width: '100%', height: '100%', objectFit: 'contain', filter: isExpired ? 'blur(4px)' : 'none' }} />
                          {isExpired && (
                            <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.85)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                              <div style={{ fontSize: 28, marginBottom: 6 }}>⏰</div>
                              <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: RED }}>二维码已过期</div>
                              <button onClick={onBack} style={{ marginTop: 10, padding: '6px 16px', border: `1px solid ${GOLD}`, borderRadius: 3, background: 'none', color: GOLD, cursor: 'pointer', fontFamily: 'var(--mono)', fontSize: 11 }}>{t('common.retry')}</button>
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={{ textAlign: 'center', padding: 20 }}>
                          <AlipayIcon size={48} />
                          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: MUTED_LIGHT, marginTop: 10 }}>二维码生成中</div>
                          <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: MUTED_LIGHT, marginTop: 4 }}>请联系客服完成充值</div>
                        </div>
                      )}
                    </div>
                    <div style={{ marginTop: 12, fontFamily: 'var(--mono)', fontSize: 14, color: '#110E0A', fontWeight: 600 }}>
                      请转账 <span style={{ color: GOLD, fontSize: 18 }}>¥{Number(amount_cny).toLocaleString()}</span>
                    </div>
                  </>
                )}
              </div>
              <div style={{ background: '#f8f5ef', borderRadius: 4, padding: '12px 16px' }}>
                {(isWechat ? [
                  { icon: '📱', text: '微信扫一扫 / 长按识别上方二维码' },
                  { icon: '💰', text: '支付成功后自动到账' },
                  { icon: '✅', text: '系统自动确认，无需人工审核' },
                ] : [
                  { icon: '📱', text: '打开支付宝，扫描上方二维码' },
                  { icon: '💰', text: '备注您的注册手机号，方便人工核对' },
                  { icon: '✅', text: '支付后系统自动识别，等待运营确认到账' },
                ]).map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'var(--mono)', fontSize: 12, color: '#110E0A', marginBottom: i < 2 ? 8 : 0 }}>
                    <span style={{ fontSize: 15, width: 22, textAlign: 'center' }}>{item.icon}</span>
                    {item.text}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 12, fontFamily: 'var(--mono)', fontSize: 11, color: MUTED_LIGHT, textAlign: 'center' }}>
                正在等待支付确认…系统每4秒自动检测
              </div>
            </>
          )}
        </div>
      </div>

      {/* 返回按钮 */}
      <button
        onClick={onBack}
        style={{
          width: '100%', padding: '12px', border: `1px solid ${BD}`, borderRadius: 4,
          background: '#fff', color: MUTED, fontFamily: 'var(--mono)', fontSize: 13,
          cursor: 'pointer', transition: 'all .15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = MUTED; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = BD; }}
      >{t('common.back')}</button>
    </div>
  );
}

/* ── 结果画面 ── */
function ResultScreen({ type, request, onAgain }) {
  const { t } = useTranslation();
  const isSuccess = type === 'paid_waiting' || type === 'success';
  return (
    <div style={{ maxWidth: 480, margin: '0 auto', textAlign: 'center', padding: '40px 20px' }}>
      <div style={{ fontSize: 64, marginBottom: 20 }}>{isSuccess ? '⏳' : '❌'}</div>
      <div style={{ fontFamily: 'var(--serif)', fontSize: 22, fontWeight: 600, color: isSuccess ? ORANGE : RED, marginBottom: 8 }}>
        {isSuccess ? '已支付，等待确认' : '充值失败'}
      </div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 13, color: MUTED_LIGHT, lineHeight: 1.8, marginBottom: 24 }}>
        {isSuccess
          ? '您的充值申请已提交，运营人员确认到账后保证金将自动到账。'
          : (request?.reject_reason || '充值申请未通过，请联系客服了解详情。')}
      </div>
      {/* 订单信息 */}
      <div style={{ background: '#fff', border: `1px solid ${BD}`, borderRadius: 6, padding: '16px 20px', marginBottom: 28, textAlign: 'left' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontFamily: 'var(--mono)', fontSize: 12, borderBottom: `1px solid rgba(232,224,208,0.5)` }}>
          <span style={{ color: MUTED_LIGHT }}>订单号</span>
          <span style={{ color: '#110E0A', fontWeight: 500, fontSize: 11 }}>{request?.id || '—'}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontFamily: 'var(--mono)', fontSize: 12, borderBottom: `1px solid rgba(232,224,208,0.5)` }}>
          <span style={{ color: MUTED_LIGHT }}>充值金额</span>
          <span style={{ color: GOLD, fontWeight: 700 }}>¥{Number(request?.amount_cny || 0).toLocaleString()}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontFamily: 'var(--mono)', fontSize: 12 }}>
          <span style={{ color: MUTED_LIGHT }}>{t('deposit.method')}</span>
          <span style={{ color: MUTED }}>{METHOD_LABEL[request?.payment_method] || '—'}</span>
        </div>
      </div>
      <button
        onClick={onAgain}
        style={{
          width: '100%', padding: 14, background: GOLD, border: 'none', borderRadius: 4,
          color: '#fff', fontFamily: 'var(--serif)', fontSize: 15, fontWeight: 600,
          letterSpacing: '0.1em', cursor: 'pointer',
        }}
      >
        {isSuccess ? '继续充值' : '重新申请'}
      </button>
    </div>
  );
}

/* ── 充值记录详情 Modal ── */
function RequestDetailModal({ record, payConfig, visible, onClose }) {
  const { t } = useTranslation();
  const [markingPaid, setMarkingPaid] = useState(false);

  if (!record) return null;
  const statusCfg = REQUEST_STATUS_MAP[record.status] || REQUEST_STATUS_MAP.unpaid;
  const isBank   = record.payment_method === 'bank_transfer';
  const isAlipay = record.payment_method === 'alipay';

  const handleMarkPaid = async () => {
    setMarkingPaid(true);
    try {
      await api.post(`/deposits/requests/${record.id}/mark-paid`);
      message.success(t('common.success'));
      onClose(true); // true = refresh list
    } catch (err) {
      message.error(err?.response?.data?.error || t('common.failed'));
    } finally {
      setMarkingPaid(false);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={() => onClose(false)}
      footer={null}
      title={<span style={{ fontFamily: 'var(--serif)', fontSize: 16, fontWeight: 600 }}>{t('deposit.detail')}</span>}
      width={480}
    >
      <div style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>
        {[
          ['订单号',   record.id],
          ['金额',     formatCNY(record.amount_cny)],
          ['支付方式', METHOD_LABEL[record.payment_method] || record.payment_method],
          ['创建时间', record.created_at ? dayjs(record.created_at).format('YYYY-MM-DD HH:mm') : '—'],
          record.paid_at ? ['支付时间', dayjs(record.paid_at).format('YYYY-MM-DD HH:mm')] : null,
        ].filter(Boolean).map(([k, v]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid rgba(232,224,208,0.4)` }}>
            <span style={{ color: MUTED_LIGHT }}>{k}</span>
            <span style={{ color: '#110E0A', fontWeight: 500, fontSize: 11, wordBreak: 'break-all', maxWidth: 260, textAlign: 'right' }}>{v}</span>
          </div>
        ))}
        {/* Status */}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid rgba(232,224,208,0.4)` }}>
          <span style={{ color: MUTED_LIGHT }}>状态</span>
          <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 2, background: statusCfg.bg, color: statusCfg.color, fontSize: 12 }}>{t(statusCfg.labelKey)}</span>
        </div>

        {/* QR code for unpaid (alipay or wechat) */}
        {(isAlipay || record.payment_method === 'wechat') && record.status === 'unpaid' && record.qr_code_url && (
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: MUTED_LIGHT, marginBottom: 10 }}>扫描下方二维码完成支付</div>
            <img src={record.qr_code_url} alt={record.payment_method === 'wechat' ? '微信支付二维码' : '支付宝二维码'} style={{ width: 180, height: 180, border: `1px solid ${BD}`, borderRadius: 4, objectFit: 'contain' }} />
          </div>
        )}

        {/* Bank info + mark-paid for unpaid bank_transfer */}
        {isBank && record.status === 'unpaid' && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: MUTED_LIGHT, marginBottom: 8 }}>汇款信息</div>
            {payConfig && [
              ['收款银行', payConfig.bank_name || '—'],
              payConfig.bank_branch ? ['支行', payConfig.bank_branch] : null,
              ['账户名',   payConfig.bank_account_name || '—'],
              ['账号',     payConfig.bank_account_no   || '—'],
              ['转账金额', formatCNY(record.amount_cny)],
            ].filter(Boolean).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid rgba(232,224,208,0.3)`, fontSize: 12 }}>
                <span style={{ color: MUTED_LIGHT }}>{k}</span>
                <span style={{ color: k === '转账金额' ? GOLD : '#110E0A', fontWeight: k === '转账金额' ? 700 : 400 }}>{v}</span>
              </div>
            ))}
            <button
              onClick={handleMarkPaid}
              disabled={markingPaid}
              style={{
                marginTop: 16, width: '100%', padding: '12px 0',
                background: markingPaid ? '#ccc' : GOLD, border: 'none', borderRadius: 4,
                color: '#fff', fontFamily: 'var(--serif)', fontSize: 14, fontWeight: 600,
                cursor: markingPaid ? 'not-allowed' : 'pointer',
              }}
            >
              {markingPaid ? '提交中…' : '✓ 汇款完成，等待审核'}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ── 余额退回（原路退款）Modal ── */
// 合规口径:这是「退款」——把可用余额原路退回到当初充值的微信支付账户,不是「提现」。
// 无手续费、无最低额;提交后进入后台审核,审核通过才真扣余额、真发起微信原路退回。
const REFUND_STATUS_MAP = {
  pending  : { label: '审核中',            color: ORANGE },
  approved : { label: '已通过 · 原路退回处理中', color: GREEN },
  rejected : { label: '已拒绝',            color: RED },
};

function RefundModal({ visible, onClose, available, onSubmitted }) {
  const [amount, setAmount]         = useState('');
  const [reason, setReason]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [myRefunds, setMyRefunds]   = useState([]);
  const [loading, setLoading]       = useState(false);

  const avail = Number(available || 0);
  const amt = parseFloat(amount) || 0;

  const fetchMy = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/refunds/my', { params: { limit: 10 } }); setMyRefunds(data.data || []); }
    catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (visible) { setAmount(''); setReason(''); fetchMy(); }
  }, [visible, fetchMy]);

  const handleSubmit = async () => {
    if (!(amt > 0)) { message.warning('请输入退回金额'); return; }
    if (amt > avail) { message.warning('退回金额超过可用余额'); return; }
    Modal.confirm({
      title: '确认申请余额退回',
      content: `将 ¥${amt.toFixed(2)} 按原充值渠道原路退回(微信/支付宝),无手续费。提交后需后台审核,通过后到账。`,
      okText: '确认申请', cancelText: '再想想',
      onOk: async () => {
        setSubmitting(true);
        try {
          await api.post('/refunds/deposit', { amount_cny: amt, reason });
          message.success('退款申请已提交,请等待审核');
          setAmount(''); setReason('');
          fetchMy();
          onSubmitted && onSubmitted();
        } catch (err) {
          message.error(err?.response?.data?.error || '操作失败,请重试');
        } finally { setSubmitting(false); }
      },
    });
  };

  const label = { fontFamily: 'var(--mono)', fontSize: 9.5, letterSpacing: '0.3em', color: MUTED_LIGHT, textTransform: 'uppercase', display: 'block', marginBottom: 8 };

  return (
    <Modal open={visible} onCancel={onClose} footer={null} width={440} title={null} centered>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 3, color: GOLD, marginBottom: 6 }}>REFUND · 余额退回</div>
      <div style={{ fontFamily: 'var(--serif)', fontSize: 20, fontWeight: 600, color: '#110E0A', marginBottom: 4 }}>余额退回</div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: MUTED_LIGHT, marginBottom: 18, lineHeight: 1.7 }}>
        可用余额按原充值渠道原路退回(微信 / 支付宝)
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', background: '#f8f5ef', border: `1px solid ${BD}`, borderRadius: 4, padding: '12px 16px', marginBottom: 16 }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: MUTED }}>当前可用余额</span>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 18, color: GREEN }}>¥{avail.toFixed(2)}</span>
      </div>

      <div style={label}>退回金额</div>
      <div style={{ position: 'relative', marginBottom: 6 }}>
        <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontFamily: 'var(--mono)', fontSize: 16, color: MUTED_LIGHT }}>¥</span>
        <input
          type="number" value={amount} min={0} step={100}
          onChange={e => setAmount(e.target.value)}
          placeholder="输入退回金额"
          style={{ width: '100%', padding: '13px 88px 13px 36px', border: `1px solid ${BD}`, borderRadius: 3, fontFamily: 'var(--mono)', fontSize: 18, color: '#110E0A', background: '#fff', outline: 'none', boxSizing: 'border-box' }}
          onFocus={e => { e.target.style.borderColor = GOLD; }}
          onBlur={e => { e.target.style.borderColor = BD; }}
        />
        <button onClick={() => setAmount(String(avail))} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', border: `1px solid ${GOLD}`, background: '#fff', color: GOLD, borderRadius: 3, padding: '5px 10px', fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer' }}>全部</button>
      </div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: MUTED_LIGHT, marginBottom: 16 }}>无手续费 · 无最低额 · 仅退回可用余额(冻结中的保证金需先解冻)</div>

      <div style={label}>退回原因(选填)</div>
      <input
        value={reason} onChange={e => setReason(e.target.value)}
        placeholder="可不填"
        style={{ width: '100%', padding: '11px 14px', border: `1px solid ${BD}`, borderRadius: 3, fontFamily: 'var(--mono)', fontSize: 13, color: '#110E0A', background: '#fff', outline: 'none', boxSizing: 'border-box', marginBottom: 16 }}
      />

      <button
        onClick={handleSubmit}
        disabled={submitting || !(amt > 0) || amt > avail}
        style={{
          width: '100%', padding: '13px', border: 'none', borderRadius: 4,
          background: (submitting || !(amt > 0) || amt > avail) ? '#d8cfc0' : GOLD,
          color: '#fff', fontFamily: 'var(--mono)', fontSize: 14, fontWeight: 600,
          letterSpacing: '0.1em', cursor: (submitting || !(amt > 0) || amt > avail) ? 'not-allowed' : 'pointer',
        }}
      >{submitting ? '提交中…' : '申请退回'}</button>

      <div style={{ marginTop: 12, fontFamily: 'var(--mono)', fontSize: 10.5, color: MUTED_LIGHT, lineHeight: 1.8, textAlign: 'center' }}>
        提交后进入后台审核,审核通过即按原充值渠道原路退回,通常 1–3 个工作日到账。
      </div>

      {/* 我的退款记录 */}
      <div style={{ marginTop: 22, borderTop: `1px solid ${BD}`, paddingTop: 14 }}>
        <div style={{ ...label, marginBottom: 10 }}>我的退款记录</div>
        <Spin spinning={loading}>
          {myRefunds.length === 0 ? (
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: MUTED_LIGHT, textAlign: 'center', padding: '12px 0' }}>暂无退款记录</div>
          ) : myRefunds.map((r) => {
            const st = REFUND_STATUS_MAP[r.status] || { label: r.status, color: MUTED };
            return (
              <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: `1px solid rgba(232,224,208,0.5)` }}>
                <div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: '#110E0A' }}>¥{Number(r.amount_cny).toFixed(2)}</div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 9.5, color: MUTED_LIGHT, marginTop: 2 }}>{dayjs(r.created_at).format('YYYY-MM-DD HH:mm')} · {r.refund_no}</div>
                </div>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: st.color }}>{st.label}</span>
              </div>
            );
          })}
        </Spin>
      </div>
    </Modal>
  );
}

/* ══════════════════════════════════════════ */
export default function Deposit() {
  const { t } = useTranslation();
  const [step, setStep]         = useState('form'); // 'form' | 'paying' | 'paid_waiting' | 'failed'
  const [request, setRequest]   = useState(null);
  const [balance, setBalance]   = useState(null);
  const [ledger, setLedger]     = useState([]);
  const [payConfig, setPayConfig] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [ledgerLoading, setLedgerLoading]   = useState(false);
  const [submitting, setSubmitting]         = useState(false);

  // Deposit request history
  const [depositRequests, setDepositRequests]       = useState([]);
  const [depositReqLoading, setDepositReqLoading]   = useState(false);
  const [detailRecord, setDetailRecord]             = useState(null);
  const [detailVisible, setDetailVisible]           = useState(false);
  const [refundVisible, setRefundVisible]           = useState(false);

  const [selectedAmount, setSelectedAmount] = useState(5000);
  const [customAmount, setCustomAmount]     = useState('5000');
  const [isCustom, setIsCustom]             = useState(false);
  const [payMethod, setPayMethod]           = useState('wechat');

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchBalance = useCallback(async () => {
    setBalanceLoading(true);
    try { const { data } = await api.get('/deposits/balance'); setBalance(data); }
    catch { message.error(t('common.failed')); }
    finally { setBalanceLoading(false); }
  }, []);

  const fetchLedger = useCallback(async () => {
    setLedgerLoading(true);
    try { const { data } = await api.get('/deposits/ledger'); setLedger(data.data || []); }
    catch { message.error(t('common.failed')); }
    finally { setLedgerLoading(false); }
  }, []);

  const fetchDepositRequests = useCallback(async () => {
    setDepositReqLoading(true);
    try {
      const { data } = await api.get('/deposits/requests', { params: { limit: 20 } });
      setDepositRequests(data.data || []);
    } catch {}
    finally { setDepositReqLoading(false); }
  }, []);

  // 微信H5支付回跳：URL 带 ?checking=<id>(新,redirect_url 回跳) 或 ?wx_order=<id>(旧) 时
  // 直接进入该订单的轮询查单流程,付完自动确认到账。
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wxOrder = params.get('checking') || params.get('wx_order');
    if (wxOrder) {
      // 清除 URL 参数
      window.history.replaceState({}, '', window.location.pathname);
      // 查询订单状态
      api.get(`/deposits/requests/${wxOrder}`).then(({ data }) => {
        setRequest(data);
        if (data.status === 'paid' || data.status === 'confirmed') {
          setStep('paid_waiting');
        } else {
          // 还未收到回调，进入轮询等待(不带 wx_h5_url,避免 PayingScreen 再次跳转微信)
          setRequest({ id: data.id, amount_cny: data.amount_cny, payment_method: 'wechat' });
          setStep('paying');
        }
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    fetchBalance();
    fetchLedger();
    fetchDepositRequests();
    api.get('/settings/payment-config').then(({ data }) => setPayConfig(data)).catch(() => {});
  }, [fetchBalance, fetchLedger, fetchDepositRequests]);

  const amount = isCustom ? (parseFloat(customAmount) || 0) : selectedAmount;
  const fee    = 0; // no fee currently
  const total  = amount + fee;

  const handleSelectAmount = (val) => {
    if (val === 0) { setIsCustom(true); setCustomAmount(''); }
    else { setIsCustom(false); setSelectedAmount(val); setCustomAmount(String(val)); }
  };

  const navigate = useNavigate();
  const handleSubmit = async () => {
    if (amount < 100) return;
    setSubmitting(true);
    try {
      const { data } = await api.post('/deposits/request', {
        amount_cny    : amount,
        payment_method: payMethod,
      });
      // 微信H5支付：wx_h5_url 传给 PayingScreen,由它带 redirect_url(?checking=<id>)
      // 跳转唤起微信 App —— 付完自动跳回充值页继续轮询查单。
      setRequest({
        id: data.id, amount_cny: amount, payment_method: payMethod,
        qr_code_url: data.qr_code_url || null,
        wx_h5_url: data.wx_h5_url || null,
      });
      setStep('paying');
      fetchDepositRequests();
    } catch (err) {
      // 未绑定手机号 → 引导完善资料后再充值
      if (err?.response?.data?.code === 'PHONE_REQUIRED') {
        message.warning('充值前请先绑定手机号');
        navigate('/complete-profile?redirect=' + encodeURIComponent('/deposit'));
        return;
      }
      message.error(err?.response?.data?.error || t('common.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuccess = useCallback((data) => {
    setRequest(r => ({ ...r, ...data }));
    setStep('paid_waiting');
    fetchBalance();
    fetchLedger();
    fetchDepositRequests();
  }, [fetchBalance, fetchLedger, fetchDepositRequests]);

  const handleAgain = () => {
    setStep('form');
    setRequest(null);
    fetchDepositRequests();
  };

  const openDetail = (rec) => {
    setDetailRecord(rec);
    setDetailVisible(true);
  };
  const closeDetail = (refresh) => {
    setDetailVisible(false);
    setDetailRecord(null);
    if (refresh) fetchDepositRequests();
  };

  const labelStyle = {
    fontFamily: 'var(--mono)', fontSize: 9.5, letterSpacing: '0.3em',
    color: MUTED_LIGHT, textTransform: 'uppercase', display: 'block', marginBottom: 10,
  };

  return (
    <div style={{ background: BG, minHeight: 'calc(100vh - 120px)', padding: '0 0 60px' }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: `1px solid ${BD}`, padding: isMobile ? '16px' : '24px 32px' }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 3, color: GOLD, marginBottom: 8 }}>DEPOSIT RECHARGE · 保証金チャージ</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h1 style={{ fontFamily: 'var(--serif)', fontSize: 26, fontWeight: 600, color: '#110E0A', letterSpacing: 2, margin: 0 }}>{t('deposit.recharge')}</h1>
            {/* Step indicator */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
              {[['form','填写信息'],['paying','支付'],['paid_waiting','等待确认']].map(([s, label], i) => {
                const stepIdx = { form: 0, paying: 1, paid_waiting: 2, failed: 2 };
                const cur = stepIdx[step] ?? 0;
                const done = cur > i;
                const active = cur === i;
                return (
                  <React.Fragment key={s}>
                    {i > 0 && <div style={{ width: 24, height: 1, background: done ? GOLD : BD }} />}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%', fontSize: 10, fontFamily: 'var(--mono)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: done || active ? GOLD : '#fff',
                        border: `1px solid ${done || active ? GOLD : BD}`,
                        color: done || active ? '#fff' : MUTED_LIGHT,
                        fontWeight: 600,
                      }}>{done ? '✓' : i + 1}</div>
                      {!isMobile && <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: active ? GOLD : done ? GOLD : MUTED_LIGHT }}>{label}</span>}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 960, margin: '0 auto', padding: isMobile ? '16px' : '32px 32px 0' }}>

        {/* Balance card */}
        <Spin spinning={balanceLoading}>
          <div style={{
            background: '#fff', border: `1px solid ${BD}`, borderRadius: 6,
            padding: '24px 28px', marginBottom: 20,
            display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 0,
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 20% 50%, rgba(184,137,42,0.04) 0%, transparent 60%)', pointerEvents: 'none' }} />
            {[
              { label: t('deposit.balance'), value: (balance?.available_cny != null && balance?.frozen_cny != null) ? (Number(balance.available_cny) + Number(balance.frozen_cny)) : balance?.available_cny, color: GOLD },
              { label: t('deposit.frozen'),     value: balance?.frozen_cny,    color: '#E8A020' },
              { label: t('deposit.available'),   value: balance?.available_cny, color: GREEN },
            ].map((item, i) => (
              <div key={item.label} style={{ position: 'relative', paddingRight: isMobile ? 0 : 24, paddingLeft: isMobile ? 0 : (i > 0 ? 24 : 0), borderLeft: (!isMobile && i > 0) ? '1px solid rgba(184,137,42,0.15)' : 'none', borderTop: (isMobile && i > 0) ? '1px solid rgba(184,137,42,0.15)' : 'none', paddingTop: isMobile ? (i > 0 ? 14 : 0) : 0, paddingBottom: isMobile ? (i < 2 ? 14 : 0) : 0 }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.3em', color: 'rgba(107,94,78,0.6)', textTransform: 'uppercase', marginBottom: 6 }}>{item.label}</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 24, color: item.color }}>
                  <span style={{ fontSize: 13, opacity: 0.7, marginRight: 4 }}>¥</span>
                  {item.value != null ? Number(item.value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '—'}
                </div>
              </div>
            ))}
          </div>
        </Spin>

        {/* 余额退回入口(原路退回微信,合规:退款非提现) */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -8, marginBottom: 14 }}>
          <button
            onClick={() => setRefundVisible(true)}
            disabled={!(Number(balance?.available_cny) > 0)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              border: `1px solid ${Number(balance?.available_cny) > 0 ? GOLD : BD}`,
              background: '#fff', color: Number(balance?.available_cny) > 0 ? GOLD : MUTED_LIGHT,
              borderRadius: 4, padding: '8px 16px', fontFamily: 'var(--mono)', fontSize: 12,
              cursor: Number(balance?.available_cny) > 0 ? 'pointer' : 'not-allowed', letterSpacing: '0.05em',
            }}
          >↩ 余额退回</button>
        </div>

        <div style={{ background: '#fff8ee', border: '1px solid #f0d080', borderRadius: 4, padding: '10px 16px', marginBottom: 24, fontFamily: 'var(--mono)', fontSize: 12, color: '#8A6010' }}>
          ⚠ 保证金按估价的 <strong>30%</strong> 冻结。委托结束后自动解冻。
        </div>

        {/* ── Main content by step ── */}
        {step === 'form' && (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 340px', gap: 24, alignItems: 'start', marginBottom: 24 }}>
            {/* Left: form */}
            <div style={{ background: '#fff', border: `1px solid ${BD}`, borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: `1px solid ${BD}`, background: 'linear-gradient(135deg,#faf8f3,#f3ede3)' }}>
                <div style={{ fontFamily: 'var(--serif)', fontSize: 15, fontWeight: 600, color: '#110E0A', letterSpacing: '0.1em' }}>{t('deposit.amount')}</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: MUTED_LIGHT, marginTop: 3 }}>{t('deposit.recharge')}</div>
              </div>
              <div style={{ padding: 20 }}>
                <div style={labelStyle}>{t('deposit.amount')}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: isMobile ? 6 : 8, marginBottom: 16 }}>
                  {QUICK_AMOUNTS.map((val) => {
                    const isActive = val === 0 ? isCustom : (!isCustom && selectedAmount === val);
                    return (
                      <button key={val} onClick={() => handleSelectAmount(val)} style={{
                        padding: '10px 8px', border: `1px solid ${isActive ? GOLD : BD}`, borderRadius: 3,
                        textAlign: 'center', cursor: 'pointer',
                        background: isActive ? 'rgba(184,137,42,0.07)' : '#fff',
                        color: isActive ? GOLD : MUTED,
                        fontFamily: 'var(--mono)', fontSize: 13, fontWeight: isActive ? 500 : 400, transition: 'all 0.18s',
                      }}>
                        {val === 0 ? '自定义' : val.toLocaleString()}
                        <div style={{ fontSize: 10, color: isActive ? GOLD : MUTED_LIGHT, marginTop: 2 }}>
                          {val === 0 ? '手动输入' : val >= 10000 ? `¥${val / 10000}万` : `¥${val}`}
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div style={labelStyle}>{t('deposit.amount')}</div>
                <div style={{ position: 'relative', marginBottom: 20 }}>
                  <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontFamily: 'var(--mono)', fontSize: 16, color: MUTED_LIGHT }}>¥</span>
                  <input
                    type="number" value={customAmount}
                    onChange={e => { setCustomAmount(e.target.value); setIsCustom(true); }}
                    placeholder="最低 100 元" min={100} step={100}
                    style={{ width: '100%', padding: '14px 14px 14px 36px', border: `1px solid ${BD}`, borderRadius: 3, fontFamily: 'var(--mono)', fontSize: 20, color: '#110E0A', background: '#fff', outline: 'none', boxSizing: 'border-box' }}
                    onFocus={e => { e.target.style.borderColor = GOLD; e.target.style.boxShadow = '0 0 0 3px rgba(184,137,42,0.08)'; }}
                    onBlur={e =>  { e.target.style.borderColor = BD;   e.target.style.boxShadow = 'none'; }}
                  />
                </div>

                <div style={labelStyle}>{t('deposit.method')}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 20 }}>
                  {PAY_METHODS.map((pm) => (
                    <button key={pm.key} onClick={() => setPayMethod(pm.key)} style={{
                      border: `1px solid ${payMethod === pm.key ? GOLD : BD}`, borderRadius: 3,
                      padding: '12px 8px', textAlign: 'center', cursor: 'pointer',
                      background: payMethod === pm.key ? 'rgba(184,137,42,0.07)' : '#fff', transition: 'all 0.18s',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>{pm.icon}</div>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: payMethod === pm.key ? GOLD : MUTED }}>{t(pm.labelKey)}</div>
                    </button>
                  ))}
                </div>

                {/* Fee breakdown */}
                <div style={{ background: '#f8f5ef', border: `1px solid ${BD}`, borderRadius: 3, padding: '14px 16px', marginBottom: 20 }}>
                  {[
                    { k: t('deposit.amount'), v: formatCNY(amount) },
                    { k: t('deposit.amount'), v: formatCNY(amount) },
                  ].map(row => (
                    <div key={row.k} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontFamily: 'var(--mono)', fontSize: 12 }}>
                      <span style={{ color: MUTED_LIGHT }}>{row.k}</span>
                      <span style={{ color: MUTED }}>{row.v}</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 3px', marginTop: 8, borderTop: `1px solid ${BD}`, fontFamily: 'var(--mono)', fontSize: 12 }}>
                    <span style={{ color: '#110E0A', fontWeight: 500 }}>{t('deposit.amount')}</span>
                    <span style={{ color: GOLD, fontSize: 15, fontWeight: 500 }}>{formatCNY(total)}</span>
                  </div>
                </div>

                <button
                  disabled={amount < 100 || submitting}
                  onClick={handleSubmit}
                  style={{
                    width: '100%', padding: 16, background: amount < 100 ? '#ccc' : GOLD,
                    border: 'none', borderRadius: 3, cursor: amount < 100 ? 'not-allowed' : 'pointer',
                    color: '#fff', fontFamily: 'var(--serif)', fontSize: 16, fontWeight: 600,
                    letterSpacing: '0.2em', transition: 'all 0.22s', opacity: amount < 100 ? 0.5 : 1,
                  }}
                  onMouseEnter={e => { if (amount >= 100) e.currentTarget.style.background = '#A07A22'; }}
                  onMouseLeave={e => { if (amount >= 100) e.currentTarget.style.background = GOLD; }}
                >
                  {submitting ? '提交中…' : `提交充值申请 ${formatCNY(total)}`}
                </button>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: MUTED_LIGHT, textAlign: 'center', marginTop: 12, lineHeight: 1.7 }}>
                  充值即视为同意《保证金管理协议》<br />
                  提交申请后请完成转账 · 运营核对后人工确认到账
                </div>
              </div>
            </div>

            {/* Right: method preview */}
            <div style={{ background: '#fff', border: `1px solid ${BD}`, borderRadius: 6, padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
                {payMethod === 'wechat' ? <WechatIcon size={48} /> : payMethod === 'alipay' ? <AlipayIcon size={48} /> : <span>🏦</span>}
              </div>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 15, fontWeight: 600, color: '#110E0A', marginBottom: 6 }}>
                {METHOD_LABEL[payMethod]}
              </div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: MUTED_LIGHT, lineHeight: 1.8 }}>
                {payMethod === 'bank_transfer'
                  ? '提交申请后将显示\n银行账户信息'
                  : payMethod === 'wechat'
                  ? '提交申请后将跳转\n微信完成支付'
                  : '提交申请后将显示\n支付宝收款二维码'}
              </div>
              <div style={{ marginTop: 20, padding: '12px 16px', background: '#f8f5ef', borderRadius: 4, fontFamily: 'var(--mono)', fontSize: 11, color: MUTED_LIGHT, textAlign: 'left', lineHeight: 1.8 }}>
                {payMethod === 'bank_transfer' ? (
                  <>⚠ 汇款备注填写注册手机号<br />⏱ 工作日 1-2 小时内到账（人工审核）</>
                ) : payMethod === 'wechat' ? (
                  <>📱 点击后跳转微信完成支付<br />✅ 支付成功后自动到账，无需人工审核</>
                ) : (
                  <>📱 扫码支付后等待运营确认<br />✅ 支付宝自动回调，无需手动操作</>
                )}
              </div>
            </div>
          </div>
        )}

        {step === 'paying' && request && (
          <div style={{ marginBottom: 24 }}>
            <PayingScreen
              request={request}
              payConfig={payConfig}
              onSuccess={handleSuccess}
              onBack={handleAgain}
            />
          </div>
        )}

        {(step === 'paid_waiting' || step === 'failed') && (
          <div style={{ background: '#fff', border: `1px solid ${BD}`, borderRadius: 6, marginBottom: 24 }}>
            <ResultScreen type={step} request={request} onAgain={handleAgain} />
          </div>
        )}

        {/* ── 充值履历 ── */}
        <div style={{ background: '#fff', border: `1px solid ${BD}`, borderRadius: 6, overflow: 'hidden', marginBottom: 24 }}>
          <div style={{ padding: '14px 20px', borderBottom: `1px solid ${BD}`, background: 'linear-gradient(135deg,#faf8f3,#f3ede3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 14, fontWeight: 600, color: '#110E0A' }}>{t('deposit.history')}</div>
            <button onClick={fetchDepositRequests} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--mono)', fontSize: 11, color: GOLD }}>{t('common.refresh')}</button>
          </div>
          <Spin spinning={depositReqLoading}>
            {depositRequests.length === 0 && !depositReqLoading ? (
              <Empty description="暂无充值记录" style={{ padding: '30px 0' }} />
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['时间','金额','方式','状态','操作'].map(h => (
                      <th key={h} style={{ padding: '9px 16px', fontFamily: 'var(--mono)', fontSize: 9.5, letterSpacing: '0.2em', color: MUTED_LIGHT, textAlign: 'left', borderBottom: `1px solid ${BD}`, background: '#f8f5ef' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {depositRequests.map((rec, i) => {
                    const sCfg = REQUEST_STATUS_MAP[rec.status] || REQUEST_STATUS_MAP.unpaid;
                    return (
                      <tr key={rec.id || i} style={{ borderBottom: i < depositRequests.length - 1 ? `1px solid rgba(232,224,208,0.5)` : 'none' }}>
                        <td style={{ padding: '11px 16px', fontFamily: 'var(--mono)', fontSize: 12, color: MUTED }}>{rec.created_at ? dayjs(rec.created_at).format('YYYY-MM-DD HH:mm') : '—'}</td>
                        <td style={{ padding: '11px 16px', fontFamily: 'var(--mono)', fontSize: 13, color: GOLD, fontWeight: 600 }}>{formatCNY(rec.amount_cny)}</td>
                        <td style={{ padding: '11px 16px', fontFamily: 'var(--mono)', fontSize: 12, color: MUTED }}>{METHOD_LABEL[rec.payment_method] || rec.payment_method}</td>
                        <td style={{ padding: '11px 16px' }}>
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 2, fontFamily: 'var(--mono)', fontSize: 10, background: sCfg.bg, color: sCfg.color }}>{t(sCfg.labelKey)}</span>
                        </td>
                        <td style={{ padding: '11px 16px' }}>
                          <button
                            onClick={() => openDetail(rec)}
                            style={{ padding: '4px 10px', border: `1px solid ${BD}`, borderRadius: 2, background: 'none', cursor: 'pointer', fontFamily: 'var(--mono)', fontSize: 11, color: GOLD }}
                          >{t('common.detail')}</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Spin>
        </div>

        {/* Transaction history */}
        <div style={{ background: '#fff', border: `1px solid ${BD}`, borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: `1px solid ${BD}`, background: 'linear-gradient(135deg,#faf8f3,#f3ede3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 14, fontWeight: 600, color: '#110E0A' }}>{t('deposit.history')}</div>
            <button onClick={fetchLedger} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--mono)', fontSize: 11, color: GOLD }}>{t('common.refresh')}</button>
          </div>
          <Spin spinning={ledgerLoading}>
            {ledger.length === 0 && !ledgerLoading ? (
              <Empty description="暂无流水记录" style={{ padding: '40px 0' }} />
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['时间','类型','金额','账后余额','备注'].map(h => (
                      <th key={h} style={{ padding: '9px 16px', fontFamily: 'var(--mono)', fontSize: 9.5, letterSpacing: '0.2em', color: MUTED_LIGHT, textAlign: 'left', borderBottom: `1px solid ${BD}`, background: '#f8f5ef' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ledger.map((row, i) => {
                    const cfg = LEDGER_TYPE_MAP[row.type] || { label: row.type, color: MUTED_LIGHT, bg: 'rgba(168,153,138,0.1)' };
                    const income = isIncome(row.type);
                    return (
                      <tr key={row.id || i} style={{ borderBottom: i < ledger.length - 1 ? `1px solid rgba(232,224,208,0.5)` : 'none' }}>
                        <td style={{ padding: '11px 16px', fontFamily: 'var(--mono)', fontSize: 12, color: MUTED }}>{row.created_at ? dayjs(row.created_at).format('YYYY-MM-DD HH:mm') : '—'}</td>
                        <td style={{ padding: '11px 16px' }}>
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 2, fontFamily: 'var(--mono)', fontSize: 10, background: cfg.bg, color: cfg.color }}>{t(cfg.labelKey)}</span>
                        </td>
                        <td style={{ padding: '11px 16px', fontFamily: 'var(--mono)', fontSize: 12, color: income ? GREEN : RED, fontWeight: 600 }}>
                          {income ? '+' : '-'}{formatCNY(Math.abs(Number(row.amount_cny || row.amount || 0)))}
                        </td>
                        <td style={{ padding: '11px 16px', fontFamily: 'var(--mono)', fontSize: 12, color: MUTED }}>{formatCNY(row.balance_after)}</td>
                        <td style={{ padding: '11px 16px', fontFamily: 'var(--mono)', fontSize: 12, color: MUTED_LIGHT }}>{row.note || row.remark || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Spin>
        </div>
      </div>

      {/* Detail Modal */}
      <RequestDetailModal
        record={detailRecord}
        payConfig={payConfig}
        visible={detailVisible}
        onClose={closeDetail}
      />

      {/* 余额退回 Modal */}
      <RefundModal
        visible={refundVisible}
        onClose={() => setRefundVisible(false)}
        available={balance?.available_cny}
        onSubmitted={() => { fetchBalance(); fetchLedger(); }}
      />
    </div>
  );
}
